import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, CheckCircle, Cancel } from "@material-ui/icons";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import moment from "moment";
import { testarConexaoEmpsis } from "../../services/kanban";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { CNPJ_MASK, formatCnpj, onlyDigits } from "../../utils/cnpj";
import { i18n } from "../../translate/i18n";

// Atalho para as chaves desta aba (settings.empsis.*).
const t = (key, options) => i18n.t(`settings.empsis.${key}`, options);

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(1),
  },
  resultado: {
    marginTop: theme.spacing(2),
    fontSize: "0.875rem",
  },
  ok: {
    color: theme.palette.success.main,
  },
  falha: {
    color: theme.palette.error.main,
  },
  divider: {
    margin: theme.spacing(4, 0, 3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  renovacao: {
    marginTop: theme.spacing(2),
    fontSize: "0.875rem",
  },
  // O layout pinta todo botão contornado com fundo escuro e texto branco, até
  // desativado. Aqui o desativado fica igual ao "Gerar e salvar" desativado.
  // "&&" sobe a especificidade acima da regra do layout.
  botaoTeste: {
    "&&.Mui-disabled": {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
      borderColor: "transparent",
    },
  },
}));

const EMPSIS_TOKEN_KEY = "empsisApiToken";

const EmpsisSettings = ({ settings = [] }) => {
  const classes = useStyles();

  // Token salvo da empresa: só exibido. Ele nasce pelo "Gerar e salvar" (e é
  // renovado sozinho pelo backend); não se digita mais à mão.
  const [token, setToken] = useState("");
  const [mostrarToken, setMostrarToken] = useState(false);
  const [testando, setTestando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Gerar token na Empsis (CNPJ + usuário + senha). A senha só vive neste
  // formulário: o backend a guarda cifrada e nunca a devolve.
  const { user } = useContext(AuthContext);
  const [gerarCnpj, setGerarCnpj] = useState("");
  const [gerarUsuario, setGerarUsuario] = useState("");
  const [gerarSenha, setGerarSenha] = useState("");
  const [gerando, setGerando] = useState(false);
  const [credenciais, setCredenciais] = useState(null);

  useEffect(() => {
    const cnpjEmpresa = onlyDigits(user?.company?.cnpj);
    if (cnpjEmpresa) setGerarCnpj((prev) => prev || formatCnpj(cnpjEmpresa));
  }, [user?.company?.cnpj]);

  useEffect(() => {
    let ativo = true;
    api
      .get("/settings/empsis/credenciais")
      .then(({ data }) => {
        if (ativo) setCredenciais(data);
      })
      .catch(() => {
        // backend antigo ou sem permissão: só não mostra o status
      });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(settings)) return;
    const tokenSetting = settings.find((s) => s.key === EMPSIS_TOKEN_KEY);
    if (tokenSetting?.value) setToken(tokenSetting.value);
  }, [settings]);

  const handleGerar = async () => {
    setGerando(true);
    try {
      const { data } = await api.post("/settings/empsis/gerar-token", {
        cnpj: onlyDigits(gerarCnpj),
        usuario: gerarUsuario.trim(),
        senha: gerarSenha,
      });
      setToken(data.token || "");
      setCredenciais(data.credentials || null);
      setGerarSenha("");
      setResultado(null);
      toast.success(
        data.autoRenew ? t("toasts.generated") : t("toasts.generatedNoRenew")
      );
    } catch (error) {
      toastError(error);
    } finally {
      setGerando(false);
    }
  };

  const podeGerar =
    onlyDigits(gerarCnpj).length === 14 && gerarUsuario.trim() && gerarSenha;

  // Testa o token salvo da empresa na consulta de imobilizados.
  const handleTest = async () => {
    setTestando(true);
    setResultado(null);
    try {
      const { data } = await testarConexaoEmpsis(token.trim());
      // Sucesso: texto montado aqui, no idioma escolhido. Falha: o motivo vem
      // da Empsis (técnico, sem tradução) e só ganha o prefixo traduzido.
      let mensagem;
      if (data?.ok) {
        mensagem =
          data.total > 0
            ? t("testResult.ok", { count: data.total })
            : t("testResult.okEmpty");
      } else {
        mensagem = t("testResult.failed", { reason: data?.mensagem || "-" });
      }
      setResultado({ ok: !!data?.ok, mensagem });
    } catch (error) {
      console.error("[EmpsisSettings] Erro ao testar:", error);
      setResultado({ ok: false, mensagem: t("errors.serverUnreachable") });
    } finally {
      setTestando(false);
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        {t("title")}
      </Typography>
      <Typography className={classes.subtitle}>
        {t("description")}{" "}
        <Link
          href="https://empsis.com.br/api/empsis/autenticacao/documentacao.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("docsLink")}
        </Link>
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <InputMask
            mask={CNPJ_MASK}
            maskChar={null}
            value={gerarCnpj}
            onChange={(e) => setGerarCnpj(e.target.value)}
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                fullWidth
                variant="outlined"
                size="small"
                label={t("fields.cnpj")}
                placeholder="00.000.000/0000-00"
              />
            )}
          </InputMask>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={t("fields.user")}
            autoComplete="off"
            value={gerarUsuario}
            onChange={(e) => setGerarUsuario(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={t("fields.password")}
            type="password"
            autoComplete="new-password"
            value={gerarSenha}
            onChange={(e) => setGerarSenha(e.target.value)}
          />
        </Grid>
      </Grid>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          disabled={gerando || !podeGerar}
          onClick={handleGerar}
          startIcon={gerando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {gerando ? t("buttons.generating") : t("buttons.generate")}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          className={classes.botaoTeste}
          disabled={testando || !token.trim()}
          onClick={handleTest}
          startIcon={testando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {testando ? t("buttons.testing") : t("buttons.test")}
        </Button>
      </div>

      {resultado && (
        <Box className={classes.resultado}>
          <Typography
            className={resultado.ok ? classes.ok : classes.falha}
            variant="body2"
          >
            {resultado.ok ? (
              <CheckCircle style={{ fontSize: 16, verticalAlign: "-0.2em", marginRight: 4 }} />
            ) : (
              <Cancel style={{ fontSize: 16, verticalAlign: "-0.2em", marginRight: 4 }} />
            )}
            {resultado.mensagem}
          </Typography>
        </Box>
      )}

      {credenciais && (
        <Typography
          variant="body2"
          className={`${classes.renovacao} ${credenciais.autoRenew ? classes.ok : ""}`}
        >
          {credenciais.autoRenew
            ? t("autoRenew.active", { user: credenciais.usuario }) +
              (credenciais.tokenGeneratedAt
                ? t("autoRenew.generatedAt", {
                    // "L LT" segue o idioma escolhido (o moment acompanha o i18n)
                    date: moment(credenciais.tokenGeneratedAt).format("L LT"),
                  })
                : "")
            : t("autoRenew.inactive")}
        </Typography>
      )}

      <Divider className={classes.divider} />

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        {t("currentToken")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={t("fields.token")}
            placeholder={t("fields.tokenPlaceholder")}
            type={mostrarToken ? "text" : "password"}
            value={token}
            InputLabelProps={{ shrink: true }}
            helperText={t("fields.tokenHelper")}
            InputProps={{
              readOnly: true,
              endAdornment: token ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setMostrarToken((prev) => !prev)}
                    edge="end"
                  >
                    {mostrarToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default EmpsisSettings;
